import { useMemo, useState } from "react";
import { BRAND } from "../lib/config";
import { Notice, useNotice } from "../components/Notice";
import { FilterBar, PageHeader, SearchField, SelectField, plural } from "../components/PageHeader";
import { Chip, EmptyState, ErrorState, Skeleton, StatusSelect } from "../components/States";
import { listBookings, setBookingStatus } from "../lib/data";
import { isAwaitingSignature } from "../lib/agreements-data";
import { OverrideConfirm } from "../components/OverrideConfirm";
import { isEndChecklistRequired } from "../modules/field/field-data";
import { CompleteOverride } from "../modules/field/ShiftChecklistStatus";
import { fmtLongDay, fmtTime, humanise, localMidnight, localYmd } from "../lib/format";
import { crewLabel, crewNeeded, crewShortfall } from "../lib/jobs";
import type { Booking } from "../lib/supabase";
import { useAsync } from "../lib/useAsync";

const STATUSES = ["requested", "confirmed", "allocated", "completed", "cancelled"];
const SHIFTS = ["day", "night", "weekend", "public_holiday"];

/** Agenda: bookings from 7 days ago onwards, grouped by Sydney calendar day. */
export function Bookings() {
  const { data, error, loading, reload } = useAsync(async () => listBookings(new Date(Date.now() - 7 * 86400_000).toISOString()));
  const { notice, setNotice, fail } = useNotice();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [shift, setShift] = useState("");
  const [range, setRange] = useState("all");

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const todayStart = localMidnight(localYmd()).toISOString();
    return (data ?? []).filter((b) => {
      if (range === "upcoming" && b.starts_at < todayStart) return false;
      if (status === "needs_crew" ? crewShortfall(b) === null : status && b.status !== status) return false;
      if (shift && b.shift_type !== shift) return false;
      if (!needle) return true;
      return [b.company, b.contact_name, b.contact_phone, b.site_address].some((v) => v?.toLowerCase().includes(needle));
    });
  }, [data, q, status, shift, range]);

  const byDay = useMemo(() => {
    const m = new Map<string, Booking[]>();
    for (const b of rows) {
      const k = fmtLongDay(b.starts_at);
      m.set(k, [...(m.get(k) ?? []), b]);
    }
    return [...m.entries()];
  }, [rows]);

  const [blocked, setBlocked] = useState<{ id: string; status: string; label: string } | null>(null);
  const [completing, setCompleting] = useState<string | null>(null);
  async function changeStatus(id: string, next: string) {
    setNotice(null);
    try {
      await setBookingStatus(id, next);
      reload();
    } catch (e) {
      const b = (data ?? []).find((x) => x.id === id);
      if (isAwaitingSignature(e)) setBlocked({ id, status: next, label: `The booking for ${b?.company ?? "this client"} on ${b ? fmtLongDay(b.starts_at) : "this day"}` });
      else if (isEndChecklistRequired(e)) setCompleting(id);
      else fail(e);
    }
  }

  const todayLabel = fmtLongDay(new Date());

  return (
    <section className="page" aria-labelledby="page-title">
      <PageHeader eyebrow="Operations" title="Bookings" meta={`Shifts grouped by day, ${BRAND.timezone.split("/").pop()!.replace(/_/g, " ")} time. Change a status from the list.`} />
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      {completing && <CompleteOverride key={completing} bookingId={completing} onCancel={() => setCompleting(null)} onDone={() => { setCompleting(null); reload(); }} />}
      {blocked && <OverrideConfirm key={blocked.id} bookingId={blocked.id} status={blocked.status} label={blocked.label} onCancel={() => setBlocked(null)} onDone={() => { setBlocked(null); reload(); }} />}

      <FilterBar count={data ? `Showing ${plural(rows.length, "booking")}` : undefined}>
        <SearchField label="Search bookings" placeholder="Search customer, contact or site" value={q} onChange={setQ} />
        <SelectField label="Dates" value={range} onChange={setRange} options={[{ value: "all", label: "From 7 days ago" }, { value: "upcoming", label: "Today onwards" }]} />
        <SelectField label="Status" value={status} onChange={setStatus} options={[
          { value: "", label: "All statuses" },
          { value: "needs_crew", label: "Needs crew" },
          ...STATUSES.map((s) => ({ value: s, label: humanise(s) })),
        ]} />
        <SelectField label="Shift" value={shift} onChange={setShift} options={[{ value: "", label: "All shifts" }, ...SHIFTS.map((s) => ({ value: s, label: humanise(s) }))]} />
      </FilterBar>

      {loading && !data && <Skeleton rows={8} columns={6} />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data && data.length === 0 && <EmptyState icon="calendar" title="No bookings in range">Requests from the website booking form appear here.</EmptyState>}
      {data && data.length > 0 && rows.length === 0 && <EmptyState icon="search" title="No matching bookings">Try a different search or filter.</EmptyState>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table table--stack table--grouped">
            <thead>
              <tr><th scope="col">Time</th><th scope="col">Shift</th><th scope="col">Crew</th><th scope="col">Customer</th><th scope="col">Site</th><th scope="col">Status</th></tr>
            </thead>
            {byDay.map(([day, items]) => (
              <tbody key={day}>
                <tr className="group-row">
                  <th scope="rowgroup" colSpan={6}>
                    {day}
                    {day === todayLabel && <span className="group-row__tag">Today</span>}
                    <span className="group-row__count">{plural(items.length, "shift")}</span>
                  </th>
                </tr>
                {items.map((b) => (
                    <tr key={b.id} className={b.status === "cancelled" ? "is-muted" : undefined}>
                      <td data-label="Time" className="nowrap strong">{fmtTime(b.starts_at)} to {fmtTime(b.ends_at)}</td>
                      <td data-label="Shift"><Chip value={b.shift_type} /></td>
                      <td data-label="Crew" className="nowrap">
                        {crewLabel(b)}
                        {b.allocations && <span className="sub">{b.allocations.length} of {crewNeeded(b)} allocated</span>}
                      </td>
                      <td data-label="Customer">
                        {b.company ?? b.contact_name ?? <span className="muted">Not set</span>}
                        {b.company && b.contact_name && <span className="sub">{b.contact_name}</span>}
                        {b.contact_phone && <a className="sub sub--link" href={`tel:${b.contact_phone.replace(/\s/g, "")}`}>{b.contact_phone}</a>}
                      </td>
                      <td data-label="Site">{b.site_address ?? <span className="muted">Not set</span>}</td>
                      <td data-label="Status">
                        <div className="status-cell">
                          <StatusSelect value={b.status} options={STATUSES} label={`Status for ${b.company ?? "booking"} at ${fmtTime(b.starts_at)}`} onChange={(v) => changeStatus(b.id, v)} />
                          {b.confirm_override_reason && <span className="sub sub--primary" title={b.confirm_override_at ? `Recorded ${fmtLongDay(b.confirm_override_at)}` : undefined}>Confirmed without signature: {b.confirm_override_reason}</span>}
                          {crewShortfall(b) !== null && <Chip tone="accent">Needs {crewShortfall(b)} more</Chip>}
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      )}
    </section>
  );
}
